"use client";

import React, { useState } from "react";
import HeroTitle from "../HeroTitle";
import Link from "next/link";
import Image from "next/image";
import { ArrowRightCircle } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";
import AgenciesPopup from "./AgenciesPopup";

function renderMarkedTitle(title: string, highlightClass: string) {
  const parts = title.split(/(\[.*?\])/g);

  return parts.map((part, index) => {
    const isHighlighted = part.startsWith("[") && part.endsWith("]");

    if (isHighlighted) {
      const text = part.slice(1, -1);

      return (
        <span
          key={index}
          className={`inline-block text-[28px] lg:text-[38px] font-heading rounded-xl -rotate-1 my-2 uppercase tracking-widest px-2 lg:px-4 py-1 text-white ${highlightClass}`}
        >
          {text}
        </span>
      );
    }

    return <span key={index}>{part}</span>;
  });
}

export default function Hero({ data }: BlockProps) {
  const [openPopup, setOpenPopup] = useState(false);
  const [agences, setAgences] = useState<any[]>([]);
  const [loadingAgences, setLoadingAgences] = useState(false);

  if (!data) return null;

  const imageUrl = data?.image?.url;
  const isAgenciesPopup =
    data?.ctaType === "popup-agences" || data?.variante === "nos-agence";

  const handleOpenPopup = async () => {
    setOpenPopup(true);
    setLoadingAgences(true);

    try {
      const endpoint = process.env.NEXT_PUBLIC_STRAPI_OUR_AGENCIES_ENDPOINT;

      if (!endpoint) {
        throw new Error("NEXT_PUBLIC_STRAPI_OUR_AGENCIES_ENDPOINT is not defined");
      }

      const url = new URL(endpoint);
      url.searchParams.set("populate", "*");
      url.searchParams.set("pagination[pageSize]", "100");

      const res = await fetch(url.toString(), {
        method: "GET",
        cache: "no-store",
      });

      if (!res.ok) {
        throw new Error("Failed to fetch agencies");
      }

      const json = await res.json();
      setAgences(json?.data || []);
      console.log("Agences récupérées :", json?.data || []);
    } catch (error) {
      console.error("Erreur récupération agences :", error);
      setAgences([]);
    } finally {
      setLoadingAgences(false);
    }
  };
  if (data.variante === "accueil") {
    return (
      <section className="relative overflow-hidden bg-white pl-6  pt-6 lg:pt-10" key={data.id}>
        <div className="mx-auto grid w-full 3xl:min-h-[600px] 2xl:min-h-[520px] grid-cols-1 lg:grid-cols-2">

          {/* Image mobile : hors flux, en haut à droite */}
          <div className="pointer-events-none absolute  right-[-30px] top-[60px] z-0 h-[260px] w-[260px] sm:right-[-70px] sm:top-[130px] sm:h-[300px] sm:w-[300px] lg:hidden">
            <Image
              src="https://site-reg.fra1.digitaloceanspaces.com/Frame_558_1_67becd1233.png"
              alt="image accueil"
              fill
              className="object-contain object-right-top"
              priority
            />
          </div>
          {/* Colonne gauche */}
          <div className="relative z-10 flex flex-col justify-center px-[3%] lg:px-12">
            
            <HeroTitle title={data.titre} />
            <p className="mt-4 max-w-[250px] lg:max-w-200 text-[18px] font-medium font-body leading-snug text-text-body md:text-[28px]">
              {data?.description}
            </p>
            <div className="mt-5 lg:mt-10">
              <Link
                href={process.env.NEXT_PUBLIC_STRAPI_COMMANDE_ENDPOINT || "#"}
                className="inline-flex items-center gap-3 rounded-full bg-yellow px-7 py-3 lg:py-4 text-[18px] lg:text-[20px] font-semibold text-black transition hover:opacity-90"
              >
                {data?.cta?.text}
                <ArrowRightCircle className="h-6 w-6" />
              </Link>
            </div>
            <div className="mt-5 lg:mt-10 pb-4 flex items-center gap-4">
              <img src="/icon-reg.svg" alt="icon regalice" width={35} height={35} />
              <p className="text-[16px] lg:text-[20px] font-literata font-light text-text-body">
                Distribué par{" "}
                <span className="font-bold">Les Menus Services</span>
              </p>
            </div>
          </div>

          {/* Colonne droite — image */}
          <div className="relative  lg:block hidden">
            <Image
              src={imageUrl}
              alt="image accueil"
              fill
              className="object-contain object-bottom"
              priority
            />
          </div>

        </div>
      </section>
    );
  }
  if (data.variante === "presentation") {
    return (
      <>
        <section className="overflow-hidden bg-blue-light" key={data.id}>
          <div className="mx-auto lg:pl-8 grid w-full 3xl:min-h-[600px] lg:min-h-[520px] grid-cols-1 lg:grid-cols-2 items-stretch">
            <div className="relative z-10 flex flex-col justify-center px-6 py-10 md:px-8 lg:px-12">
              <h1 className="font-literata text-[38px] lg:text-[48px] font-bold leading-[1.30]">
                {renderMarkedTitle(data.titre, "bg-pink")}
              </h1>

              <p className=" text-[18px] lg:text-[22px] font-body font-medium leading-snug text-text-body ">
                {data?.description}
              </p>

              <div className="mt-5">

                <button
                  type="button"
                  onClick={handleOpenPopup}
                  disabled={loadingAgences}
                  className="inline-flex items-center gap-3 rounded-full bg-yellow px-7 py-4 text-[18px] font-semibold text-black transition hover:opacity-90 disabled:opacity-50"
                >
                  {loadingAgences ? "Chargement..." : data?.cta?.text}
                  <ArrowRightCircle className="h-6 w-6" />
                </button>

              </div>
            </div>
  
            <div className="relative min-h-[300px] lg:block hidden">
              {imageUrl && (
                <Image
                  src={imageUrl}
                  alt={data.image?.alternativeText || "image présentation agences"}
                  fill
                  className="object-contain "
                  priority
                />
              )}
            </div>
          </div>
        </section>

        <AgenciesPopup
          isOpen={openPopup}
          onClose={() => setOpenPopup(false)}
          agences={agences}
        />
      </>
    );
  }
  if (data.variante === "autres-zones") {
    return (
      <>
        <section className="relative overflow-hidden" key={data.id}>
          {/* Partie image / hero */}
          <div className="relative min-h-[420px] md:min-h-[520px] lg:min-h-[620px]">
            <Image
              src={imageUrl}
              alt="image portrait"
              fill
              className="object-cover object-center"
              priority
            />

            {/* overlay */}
            <div className="absolute inset-0 bg-black/35" />

            {/* contenu sur image */}
            <div className="relative z-10 flex min-h-[420px] flex-col justify-center px-6 py-12 md:min-h-[520px] md:px-10 lg:min-h-[620px] lg:px-16">
              <div className="">
                <h1 className="font-literata text-[28px] lg:text-6xl text-center font-bold leading-tight text-white ">
                  {data.titre}
                </h1>

                <div className="mt-8 text-center">
                  <Link
                    href={data?.cta?.link || "#"}
                    className="inline-flex items-center gap-3 rounded-full bg-yellow px-7 py-3 lg:py-4 text-[18px] font-semibold text-black transition hover:opacity-90 md:text-[20px]"
                  >
                    {data?.cta?.text}
                    <ArrowRightCircle className="h-6 w-6" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Partie description en dessous */}
          <div className="bg-white  py-8">
            <div className="container mx-auto">
              <p className="text-lg font-medium leading-relaxed text-text-body md:text-[24px]">
                {data?.description}
              </p>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <section className="overflow-hidden bg-blue-light" key={data.id}>
      <div className="mx-auto lg:pl-8 grid w-full 3xl:min-h-[600px] 2xl:min-h-[520px] grid-cols-1 lg:grid-cols-2 items-stretch">
        <div className="relative z-10 flex flex-col justify-center px-6 py-10 md:px-8 lg:px-12">
          <h1 className="font-literata text-[32px] lg:text-[44px] font-bold leading-[1.30]">
            {renderMarkedTitle(data.titre, "bg-pink")}
          </h1>
          <p className="  text-[18px] lg:text-[22px] font-body font-medium leading-snug text-text-body ">
            {data?.description}
          </p>
          <div className="mt-5">
            <Link
              href={data?.cta?.link || "#"}
              className="inline-flex items-center gap-3 rounded-full bg-yellow px-7 py-3 lg:py-4 text-[18px] lg:text-[20px] font-semibold text-black transition hover:opacity-90"
            >
              {data?.cta?.text}
              <ArrowRightCircle className="h-6 w-6" />
            </Link>
          </div>
        </div>
        

        <div className="relative  lg:block hidden min-h-[300px]">
          {imageUrl && (
            <Image
              src={imageUrl}
              alt="image hero"
              fill
              className="object-contain object-right-bottom"
              priority
            />
          )}
        </div>
      </div>
    </section>
  );
}